import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Search, X, CornerDownLeft } from "lucide-react";
import TrackingModal from "../DashBoard/TrackingModal";
import { Kbd, Pill, Spinner, fmtINR } from "../ui/caliper";

/* Global order search — lives in the topbar, opens a ⌘K command palette.
 * Replaces the standalone search panel that used to sit on the Dashboard. */
const HeaderSearch = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const [openTracking, setOpenTracking] = useState(false);
  const [selected, setSelected] = useState(null);

  const inputRef = useRef(null);

  /* ⌘K / Ctrl+K to open, Esc to close */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
  }, [open]);

  const runSearch = useCallback(async () => {
    const v = value.trim();
    if (!v) {
      setResults([]);
      setSearched(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(`${apiUrl}search`, { params: { value: v } });
      setResults(Array.isArray(data?.successList) ? data.successList : []);
    } catch (err) {
      console.error("Order search failed", err);
      setResults([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, [value, apiUrl]);

  const close = () => setOpen(false);

  const viewItem = (item) => {
    setSelected(item);
    setOpen(false);
    setOpenTracking(true);
  };

  return (
    <>
      {/* topbar trigger */}
      <button
        onClick={() => setOpen(true)}
        title="Search (⌘K)"
        className="flex items-center justify-center md:justify-start gap-2 h-8 w-8 md:h-7 md:w-auto px-0 md:px-2.5 md:ml-3 rounded-cb-sm border border-hairline bg-sunken text-fg-muted hover:bg-surface-hover transition-colors shrink-0 md:shrink md:max-w-[320px] md:flex-1 md:min-w-[150px]"
      >
        <Search size={14} strokeWidth={1.75} className="shrink-0" />
        <span className="text-t-sm truncate hidden md:inline">Search order, enquiry, patient…</span>
        <Kbd className="ml-auto shrink-0 hidden md:inline-flex">⌘K</Kbd>
      </button>

      {/* search palette */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-[6vh] sm:pt-[11vh] px-2 sm:px-4 cb-fade-in"
          style={{ background: "var(--bg-overlay)" }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div className="w-[700px] max-w-full bg-panel border border-hairline-subtle rounded-cb-lg shadow-cb-lg overflow-hidden cb-animate-in flex flex-col max-h-[72vh]">
            {/* input row */}
            <div className="flex items-center gap-2.5 px-3.5 h-12 border-b border-hairline-subtle">
              <Search size={16} strokeWidth={1.75} className="text-fg-muted shrink-0" />
              <input
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="Order ID, enquiry ID, consignment ID, patient ID, mobile…"
                className="flex-1 min-w-0 bg-transparent outline-none border-0 text-t-base text-fg-default placeholder:text-fg-subtle"
              />
              {loading && <Spinner size={15} />}
              {value && !loading && (
                <button
                  onClick={() => {
                    setValue("");
                    setResults([]);
                    setSearched(false);
                    inputRef.current?.focus();
                  }}
                  className="text-fg-muted hover:text-fg-strong"
                  aria-label="Clear"
                >
                  <X size={15} strokeWidth={2} />
                </button>
              )}
              <span className="w-px h-5 bg-hairline" />
              <button
                onClick={close}
                className="text-fg-muted hover:text-fg-strong"
                aria-label="Close"
              >
                <X size={16} strokeWidth={1.9} />
              </button>
            </div>

            {/* results */}
            <div className="overflow-auto cb-scroll flex-1">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-14 text-fg-muted text-t-sm">
                  <Spinner size={15} /> Searching…
                </div>
              ) : results.length > 0 ? (
                <table className="w-full border-collapse min-w-[560px]">
                  <thead>
                    <tr className="sticky top-0 bg-surface-subtle">
                      <th className="cb-th text-left px-3.5 py-2 border-b border-hairline">Order ID</th>
                      <th className="cb-th text-left px-3 py-2 border-b border-hairline">Enquiry ID</th>
                      <th className="cb-th text-left px-3 py-2 border-b border-hairline">Customer</th>
                      <th className="cb-th text-right px-3 py-2 border-b border-hairline">Amount</th>
                      <th className="cb-th text-left px-3 py-2 border-b border-hairline">Source</th>
                      <th className="cb-th px-3 py-2 border-b border-hairline w-[68px]" />
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((item, i) => (
                      <tr
                        key={i}
                        className="border-b border-hairline-subtle last:border-0 hover:bg-surface-hover transition-colors"
                      >
                        <td className="px-3.5 py-2 font-mono text-t-sm font-semibold text-fg-strong">
                          {item.orderId || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-t-sm text-fg-muted">
                          {item.enquiryId || "—"}
                        </td>
                        <td className="px-3 py-2 text-t-sm">
                          <div className="text-fg-default">{item.name || "—"}</div>
                          <div className="text-t-xs text-fg-muted font-mono">
                            {item.customerId || ""}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-t-sm font-semibold">
                          {item.orderAmount != null ? fmtINR(item.orderAmount) : "—"}
                        </td>
                        <td className="px-3 py-2">
                          {item.sourceChannel ? (
                            <Pill variant="neutral">{item.sourceChannel}</Pill>
                          ) : (
                            <span className="text-fg-subtle">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => viewItem(item)}
                            className="text-t-sm font-semibold text-fg-link hover:underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : searched ? (
                <div className="flex flex-col items-center gap-1.5 py-14 text-center">
                  <Search size={24} strokeWidth={1.5} className="text-fg-subtle" />
                  <div className="text-t-md font-medium text-fg-default">No results found</div>
                  <div className="cb-caption">Check the ID or number and try again.</div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1.5 py-14 text-center">
                  <Search size={24} strokeWidth={1.5} className="text-fg-subtle" />
                  <div className="text-t-md font-medium text-fg-default">Search for an order</div>
                  <div className="cb-caption max-w-sm">
                    Look up by order, enquiry or consignment ID, patient ID or mobile
                    number, then press Enter.
                  </div>
                </div>
              )}
            </div>

            {/* footer */}
            <div className="flex items-center justify-between px-3.5 py-2 border-t border-hairline-subtle text-t-xs text-fg-muted">
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <CornerDownLeft size={10} strokeWidth={2} />
                </Kbd>
                to search
              </span>
              <span>
                {results.length > 0
                  ? `${results.length} result${results.length > 1 ? "s" : ""}`
                  : "Global order search"}
              </span>
            </div>
          </div>
        </div>
      )}

      <TrackingModal
        isOpen={openTracking}
        onClose={() => {
          setOpenTracking(false);
          setSelected(null);
        }}
        item={selected}
      />
    </>
  );
};

export default HeaderSearch;
